import styled from 'styled-components';
import { AlertButtonType } from '../../../../interfaces/Element.interfaces';
import { getColorHexCode } from '../../../../utils/getter/getColorHexCode.util';
import { DimmedBackground } from '../../dimmed/DimmedBackground';
import { Icon } from '../../icon/Icon';
import { SpinnerLoader } from '../../loading/SpinnerLoader';

interface PopupConfirmModalProps {
  isShow: boolean;
  hasCloseButton?: boolean;
  closeModal?: () => void;
  onClose?: () => void;
  title?: string;
  description?: string;
  element?: JSX.Element;
  buttonArray?: AlertButtonType[];
  isDescriptionLeftAligned?: boolean;
}

export const PopupConfirmModal = ({
  isShow,
  closeModal,
  onClose,
  hasCloseButton,
  title,
  description,
  element,
  buttonArray,
  isDescriptionLeftAligned = false,
}: PopupConfirmModalProps) => {
  const handleModalClose = () => {
    if (closeModal) {
      closeModal();
      onClose?.();
    }
  };

  return (
    <DimmedBackground isShow={isShow} onClick={handleModalClose}>
      <Container
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {hasCloseButton && (
          <CloseButtonWrap>
            <CloseButton onClick={handleModalClose}>
              <Icon name={'CloseGrey'} width={20} height={20} />
            </CloseButton>
          </CloseButtonWrap>
        )}

        <BodyContaier>
          {title && <Title>{title}</Title>}
          {description && <Content isDescriptionLeftAligned={isDescriptionLeftAligned}>{description}</Content>}
          {element && element}
        </BodyContaier>

        <ButtonContainer>
          {buttonArray?.map((button) => {
            return (
              <Button
                key={button.text}
                onClick={button.onClick}
                backgroundColor={getColorHexCode({ type: 'buttonBackground', color: button.color })}
                disabled={button.disabled}
              >
                {button.icon}
                <SpinnerLoader isVisible={button.isLoading ?? false} />
                {!button.isLoading && (
                  <ButtonText color={getColorHexCode({ type: 'buttonText', color: button.color })}>
                    {button.text}
                  </ButtonText>
                )}
              </Button>
            );
          })}
        </ButtonContainer>
      </Container>
    </DimmedBackground>
  );
};

const Container = styled.div<{ height?: string }>`
  width: 300px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 24px;

  gap: 24px;

  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const CloseButtonWrap = styled.div`
  width: 100%;
  height: 20px;

  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CloseButton = styled.button``;

const BodyContaier = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 24px;
`;

const Title = styled.h1`
  padding-top: 16px;

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
  white-space: pre-wrap;
`;

const Content = styled.pre<{ isDescriptionLeftAligned: boolean }>`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: ${(props) => (props.isDescriptionLeftAligned ? 'left' : 'center')};

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: ${(props) => (props.isDescriptionLeftAligned ? '150%' : '130%')};

  white-space: pre-wrap;
`;

const ButtonContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 8px;
`;

const Button = styled.button<{ backgroundColor: string }>`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: 16px;

  gap: 10px;

  border-radius: 8px;

  background-color: ${(props) => props.backgroundColor};

  &:disabled {
    opacity: 0.3;
  }
`;

const ButtonText = styled.span<{ color: string }>`
  color: ${(props) => props.color};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
